import { useRoute, useRouter, useState } from '#app'
import { computed } from 'vue'
import { type Router } from 'vue-router'

import {
  type MenuItem,
  getSidebar,
} from '@backmarket/http-api/src/api-specs-navigation-experience/navigation/sidebar'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { type Logger } from '@backmarket/nuxt-module-logger/types'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import { linkToUrl } from '../linkToUrl'

export interface Item {
  children: Item[] | never[]
  color?: string
  icon?: string
  id: number
  image?: string
  title: string
  url: string
}

function mapMenuItemtoItem(
  item: MenuItem,
  router: Router,
  logger: Logger,
): Item {
  return {
    children: item.children.map((elt) =>
      mapMenuItemtoItem(elt, router, logger),
    ),
    color: item.style?.text_color,
    icon: undefined,
    id: item.id,
    image: item.image,
    title: item.title,
    url: linkToUrl(item.link, router, logger),
  }
}

export function useNavigation() {
  const items = useState<Item[]>('menu-items', () => [])
  const router = useRouter()
  const logger = useLogger()
  const fetchMenuItems = async () => {
    if (items.value.length === 0) {
      const { data } = await useHttpFetch(getSidebar)

      if (data.value) {
        items.value = data.value.map((item) =>
          mapMenuItemtoItem(item, router, logger),
        )
      }
    }
  }

  const activeMenu = useState<number | undefined>()
  const isMenuActive = computed(() => activeMenu.value !== undefined)
  const setActiveMenu = (id: number) => {
    activeMenu.value = id
  }
  const clearActiveMenu = () => {
    activeMenu.value = undefined
  }

  const isOpen = useState('burger-menu-isOpen', () => false)
  const state = computed(() => (isOpen.value ? 'open' : 'closed'))
  const toggle = () => {
    isOpen.value = !isOpen.value
  }

  const isActivePage = (link: string | undefined) => {
    if (!link) return false
    const route = useRoute()
    try {
      const url = new URL(link)

      return route.fullPath === url.pathname
    } catch (err) {
      return route.fullPath === link
    }
  }

  return {
    clearActiveMenu,
    fetchMenuItems,
    setActiveMenu,
    toggle,
    isActivePage,
    activeMenu,
    isMenuActive,
    isOpen,
    items,
    menu: items,
    state,
  }
}
